/**
 * ONE sign-out routine for every exit path, in TWO tiers chosen by {@link SignOutReason}.
 *
 * The identity tier (session + tokens, buffered telemetry, the cache-first localStorage layer)
 * runs on every reason. The CONTENT tier — baked mockups, test artwork, template images, synced
 * template records and the hydration memos that describe them — runs on a deliberate sign-out
 * and on a different account signing in, and NOT on an involuntary expiry. See
 * {@link SignOutReason} for why those are not the same event (AUD-8 LB-12).
 *
 * Before this existed the two paths cleared different things: the button cleared the
 * localStorage cache, expiry cleared only the session, and NEITHER touched the device
 * stores. On a shared machine the next account inherited the previous one's workspace
 * settings, template options, customer lists, synced templates, baked mockup pixels,
 * uploaded test artwork, queued jobs and output folder — all cache-first, so it was
 * rendered as if it were theirs. Everything account-scoped is dropped here.
 *
 * DELIBERATELY KEPT: the remembered EMAIL (a convenience, not a credential — the password
 * is no longer stored at all) and device-scoped preferences that are about the machine
 * rather than the account (render backend, surface/theme, onboarding progress).
 *
 * Best-effort by design: every step is individually guarded so one failure can never leave
 * the user half-signed-out.
 */
import type { PlatformIO } from "@genz/platform";
import type { EngineBinding } from "@genz/engine-bind";
import { clearAllCache } from "../stores/cache.js";
import { useSessionStore } from "../stores/session.js";
import { purgeAllMockups } from "../stores/mockupStore.js";
import { purgeAllTestDesigns } from "../stores/testDesignStore.js";
import { purgeAllTemplateImages } from "../stores/imageStore.js";
import { clearAllHydration } from "../stores/hydrationStore.js";
import { clearCaseImageCache } from "../stores/caseImage.js";
import { resetGenerateDraft } from "../stores/generateDraft.js";
import { useWizardStore } from "../stores/wizard.js";
// The device-local storage `localTemplates` OWNS — named there, so a cache added alongside
// the synced templates cannot be left out of this purge (closer 1).
import { LOCAL_TEMPLATE_STORAGE } from "../templates/localTemplates.js";

/** The app's default (ungated) route — `App.tsx`'s `readHash` fallback. */
const BROWSE_ROUTE = "browse";

export interface SignOutDeps {
  io?: PlatformIO;
  engine?: EngineBinding;
  /** Telemetry/log buffers to drop so account A's events can't flush under B's token. */
  purgeBuffers?: () => Promise<void> | void;
  /**
   * Client-observability sink (`AppLogger.log`). Optional for the DI seam; when absent the
   * purge-failure report still reaches `console.error`, because a sign-out that silently
   * failed to delete the previous account's renders must never be quiet.
   */
  log?: (level: string, message: string, ctx?: Record<string, unknown>) => void;
}

/**
 * WS-Q / AUD-8 LB-12: WHY the session is ending. The two events are not the same event.
 *
 * `"user"` — a deliberate sign-out, possibly on a shared machine. Everything account-scoped
 *   goes, exactly as before: the reasoning at `stores/mockupStore.ts`'s `purgeAllMockups` is
 *   sound and is preserved.
 * `"expired"` — the refresh chain died while the operator was working. The tokens are
 *   worthless, but the ~1.5 GB of baked mockups on this disk are still THEIRS, still valid,
 *   and re-downloading them costs 0.6-4.7 s per ~1 MB object off a rate-limited `pub-*.r2.dev`
 *   endpoint (`templates/bakedSync.ts`) — a re-hydrate that can itself partially fail, which is
 *   a second route into O-4's blank-mockup symptom. Nothing is safer for having deleted them.
 * `"account-changed"` — a DIFFERENT customer signed in on this device. This is the case the
 *   content purge actually exists for, and it now fires HERE rather than being a lucky side
 *   effect of every expiry (see `reauth.ts`).
 */
export type SignOutReason = "user" | "expired" | "account-changed";

export interface SignOutOptions { reason: SignOutReason }

/**
 * Does this reason destroy the operator's CONTENT, or only their identity?
 *
 * Only an expiry keeps it. Everything else is a hand-over of the machine, real or possible.
 */
function purgesContent(reason: SignOutReason): boolean {
  return reason !== "expired";
}

/** The purge currently running, if any (see {@link signOutEverywhere}'s single-flight note). */
let purging: Promise<void> | null = null;
/** The reason the in-flight purge is running under — a joiner may need MORE than it destroys. */
let purgingReason: SignOutReason = "user";

/**
 * Sign out / purge the workspace. SINGLE-FLIGHT: while a purge is running, further calls JOIN
 * it instead of starting their own.
 *
 * Expiry is a burst, not an event: a dead session 401s the refresh AND every request that was
 * already in flight, and `jsonClient` fires `onAuthExpired` for each of them — so bootstrap
 * kicked off up to seven of these concurrently. They then raced each other and the app,
 * re-clearing stores a sibling purge had already emptied while the UI was reacting to the
 * session clear, and re-firing `purgeBuffers` (a network/IDB flush) once per failed request.
 * The routine's steps are individually idempotent; running them SIMULTANEOUSLY is what wasn't.
 *
 * Only the in-flight window is shared — a later sign-out (or a later expiry after re-login)
 * starts a fresh purge. A joiner's `deps` are ignored, which is exactly right for the burst
 * case: every caller in it carries the same io/engine/buffers. A joiner's REASON is NOT
 * ignored: an in-flight expiry deliberately KEEPS the content, so satisfying a Sign out click
 * with it would leave account A's bytes for whoever signs in next. Such a caller waits for the
 * expiry purge and then runs the content tier itself.
 *
 * `opts` is optional and defaults to `"user"` — the DESTRUCTIVE reason — so an un-migrated call
 * site keeps today's behaviour. Getting that default backwards is strictly worse than the bug
 * being fixed: it would leave account A's workspace visible to account B on a shared machine.
 */
export function signOutEverywhere(deps: SignOutDeps = {}, opts?: SignOutOptions): Promise<void> {
  const reason = opts?.reason ?? "user";
  if (purging) {
    if (!purgesContent(reason) || purgesContent(purgingReason)) return purging;
    const escalated = purging.then(() => purgeContent(deps)).finally(() => {
      if (purging === escalated) { purging = null; }
    });
    purging = escalated;
    purgingReason = reason;
    return escalated;
  }
  const run = purgeWorkspace(deps, reason).finally(() => {
    if (purging === run) purging = null; // never leave the guard held by a settled purge
  });
  purging = run;
  purgingReason = reason;
  return run;
}

/** The purge itself — always reached through the single-flight wrapper above. */
async function purgeWorkspace(deps: SignOutDeps, reason: SignOutReason): Promise<void> {
  await purgeIdentity(deps, reason);
  if (purgesContent(reason)) await purgeContent(deps);
}

/**
 * Tier 1-3 — the IDENTITY. Runs on every reason without exception: the tokens must go, a
 * buffered event must never flush under the next identity, and the cache-first layer is where
 * account A's settings, template options and admin lists live.
 */
async function purgeIdentity(deps: SignOutDeps, reason: SignOutReason): Promise<void> {
  const { purgeBuffers } = deps;

  // 0. WS-ERR F-11 — SUSPEND LOGGING FIRST, before anything else in this routine moves.
  //
  //    `purgeBuffers` is the logger's hook, and it is what stops durable writes (see
  //    `observability/logger.ts`). It used to run third — after the session clear and the route
  //    rewrite — and both of those re-render the app (a route rewrite swaps the whole page).
  //    Every render can raise a capture, and those captures landed in the very stores this
  //    routine was about to clear: the race the suspend exists to close, left open by its own
  //    ordering. It also drains writes already in flight, so nothing from the outgoing account
  //    can settle after the purge.
  //
  //    Purging before the session clear is safe in the other direction too: the buffers are
  //    DROPPED, never sent, and `flush()` no-ops while suspended — so there is no window in
  //    which the outgoing identity's events could go anywhere.
  try { await purgeBuffers?.(); } catch (e) { report(deps, "buffers", e); }

  // 1. Session + tokens, so nothing below can flush under the outgoing identity.
  try { useSessionStore.getState().clear(); } catch (e) { report(deps, "session", e); }

  // 1b. Reset the route. The hash survives sign-out, so signing out from #admin left the
  //     NEXT sign-in landing on a gated route — an empty page for a plain customer, and a
  //     deep-link into admin tooling for anyone else who used the machine.
  //     NOT on an expiry: there is no hand-over, the same operator re-authenticates in place,
  //     and dropping them on #browse would lose the run they were in the middle of.
  if (purgesContent(reason)) {
    try {
      if (typeof window !== "undefined" && window.location.hash.replace("#", "") !== BROWSE_ROUTE) {
        window.location.hash = BROWSE_ROUTE;
      }
    } catch (e) { report(deps, "route", e); }
  }

  // 2. The cache-first localStorage layer (settings, options, appearance, admin lists).
  try { clearAllCache(); } catch (e) { report(deps, "cache", e); }
}

/**
 * Tier 4 — the CONTENT. Everything on this device that holds the account's own bytes.
 *
 * Skipped on an expiry, which is the whole of LB-12. The hydration memos are IN this tier and
 * not the identity one: they describe those mockups, so keeping the mockups while dropping
 * their memos would make the next sync re-download what is already on disk.
 */
async function purgeContent(deps: SignOutDeps): Promise<void> {
  const { io, engine } = deps;
  try { clearAllHydration(); } catch (e) { report(deps, "hydration", e); }
  // The classic path's case-photo memo: in-memory like the hydration memos, and holding the
  // previous account's product photography, so it goes in the CONTENT tier with them.
  try { clearCaseImageCache(); } catch (e) { report(deps, "case-images", e); }
  // The in-progress Generate and wizard runs: the previous account's photos, jobs and Details.
  try { resetGenerateDraft(); useWizardStore.getState().reset(); } catch (e) { report(deps, "runs", e); }
  // Started LAZILY and each behind its own try/catch: a tier that throws SYNCHRONOUSLY (a
  // PlatformIO without a store, an engine binding that is already torn down) would otherwise
  // escape `Promise.allSettled` entirely and abandon the tiers after it — a half-purge, which
  // is the one outcome this routine is guarded everywhere else to prevent.
  const tiers: Array<[string, () => Promise<unknown>]> = [
    ["mockups", () => purgeAllMockups()],
    ["test-designs", () => purgeAllTestDesigns()],
    ["template-images", () => (engine ? purgeAllTemplateImages(engine) : Promise.resolve())],
    ["synced-templates", () => io?.localStore
      ? io.localStore.records.clear(LOCAL_TEMPLATE_STORAGE.records)
      : Promise.resolve()],
    // The cached phone-model / case-style catalogs. Platform-global, so no account's data
    // leaks through them — but they are storage this device wrote FOR an account, and the
    // contract above is that everything account-scoped goes. One tier, so a `kv` that refuses
    // is reported once and the tiers around it still run.
    ["catalog-cache", () => io?.localStore
      ? Promise.all(LOCAL_TEMPLATE_STORAGE.kv.map((k) => io.localStore.kv.delete(k)))
      : Promise.resolve()],
    // The per-device "removed from this device" list. It is ACCOUNT-scoped — the ids are one
    // account's template ids — and it outlived the account that wrote it, so the next sign-in
    // inherited a hide-list for templates it has never seen. On the Templates page that is a
    // stale badge; on the Mock-ups page, whose reconcile refuses to pull a hidden id back, it
    // is rows silently withheld from an account that never hid anything.
    ["device-hidden", () => {
      if (typeof localStorage !== "undefined") {
        for (const k of LOCAL_TEMPLATE_STORAGE.local) localStorage.removeItem(k);
      }
      return Promise.resolve();
    }],
  ];
  const settled = await Promise.allSettled(tiers.map(([, start]) => {
    try { return start(); } catch (e) { return Promise.reject(e); }
  }));
  settled.forEach((r, i) => { if (r.status === "rejected") report(deps, tiers[i][0], r.reason); });
}

/**
 * Report a tier that did NOT come clean.
 *
 * `Promise.allSettled` drops rejections on the floor, and every step here is individually
 * guarded so one failure can never leave the user half-signed-out — which is right, and which
 * used to mean a sign-out that failed to delete the previous account's product renders said
 * nothing at all. Best-effort must not mean unobservable: the purge still completes, and the
 * failure is named, per tier, at `error` level.
 */
function report(deps: SignOutDeps, tier: string, cause: unknown): void {
  const detail = cause instanceof Error ? cause.message : String(cause);
  const message = `sign-out: the ${tier} tier could not be cleared — ${detail}`;
  try { deps.log?.("error", message, { tier }); } catch { /* a broken sink must not block */ }
  try { console.error(message); } catch { /* nor a broken console */ }
}
